import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Check, Ban, Eye, Plus, Save } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { StatusBadge, Empty } from "@/components/common";
import { useLang } from "@/lib/i18n";
import { api, eur, errMsg, photoUrl, fmtDate } from "@/lib/api";
import { toast } from "sonner";

export function AdminListings({ onChange }) {
  const { t } = useLang();
  const [status, setStatus] = useState("review");
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/listings", { params: status === "all" ? {} : { status } }).then((r) => setItems(r.data)), [status]);
  useEffect(() => { load(); }, [load]);
  const setSt = async (id, s) => { try { await api.post(`/admin/listings/${id}/status`, { status: s }); toast.success(t(`s_${s}`)); load(); onChange?.(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className="h-11 w-56 rounded-full bg-white" data-testid="admin-listing-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent>{["review", "published", "suspended", "draft", "all"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Tutti" : t(`s_${s}`)}</SelectItem>)}</SelectContent>
      </Select>
      {!items.length && <Empty text="Nessun annuncio in questo stato." testid="admin-no-listings" />}
      <div className="grid gap-3">
        {items.map((l) => (
          <div key={l.listing_id} className="flex flex-col sm:flex-row gap-4 rounded-2xl bg-white border border-slate-200 p-3 sm:p-4" data-testid={`admin-listing-${l.listing_id}`}>
            <div className="h-36 sm:h-20 sm:w-28 shrink-0 overflow-hidden rounded-xl bg-slate-100">{l.photos?.[0] && <img src={photoUrl(l.photos[0])} alt="" className="h-full w-full object-cover" />}</div>
            <div className="flex-1 min-w-0">
              <StatusBadge status={l.status} testid={`admin-listing-status-${l.listing_id}`} />
              <p className="mt-1 font-semibold text-[#0B192C] truncate">{l.title}</p>
              <p className="text-sm text-slate-600">{l.city} · {eur(l.price_month)} → {eur(l.price_month_public)} · {l.owner?.company_name || l.owner?.name} ({t(`r_${l.owner?.role}`)})</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/listing/${l.listing_id}`} className="h-10 inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 text-sm font-semibold" data-testid={`admin-view-${l.listing_id}`}><Eye className="h-4 w-4" />{t("view")}</Link>
              {l.status !== "published" && <button onClick={() => setSt(l.listing_id, "published")} className="h-10 inline-flex items-center gap-1 rounded-full bg-green-600 px-3 text-sm font-bold text-white" data-testid={`admin-approve-${l.listing_id}`}><Check className="h-4 w-4" />Approva</button>}
              {l.status !== "suspended" && <button onClick={() => setSt(l.listing_id, "suspended")} className="h-10 inline-flex items-center gap-1 rounded-full border border-red-300 px-3 text-sm font-bold text-red-600" data-testid={`admin-suspend-${l.listing_id}`}><Ban className="h-4 w-4" />Sospendi</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminUsers() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const upd = async (id, body) => { try { await api.put(`/admin/users/${id}`, body); toast.success("Utente aggiornato"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid gap-2" data-testid="admin-users">
      {items.map((u) => (
        <div key={u.user_id} className="flex flex-wrap items-center gap-3 rounded-2xl bg-white border border-slate-200 p-4" data-testid={`admin-user-${u.user_id}`}>
          <div className="flex-1 min-w-[200px]">
            <p className="font-semibold text-[#0B192C]">{u.name} {u.company_name && <span className="text-slate-500 font-normal">· {u.company_name}</span>}</p>
            <p className="text-sm text-slate-500">{u.email}{u.vat_number ? ` · P.IVA ${u.vat_number}` : ""} · {fmtDate(u.created_at)}</p>
          </div>
          {u.role === "admin" ? <span className="rounded-full bg-[#142B5C] px-3 py-1 text-xs font-bold text-white">{t("r_admin")}</span> : (
            <>
              <Select value={u.role} onValueChange={(v) => upd(u.user_id, { role: v })}>
                <SelectTrigger className="h-10 w-52 rounded-full" data-testid={`admin-user-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{["tenant", "owner", "agency"].map((r) => <SelectItem key={r} value={r}>{t(`r_${r}`)}</SelectItem>)}</SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-sm">Attivo<Switch checked={!u.disabled} onCheckedChange={(v) => upd(u.user_id, { disabled: !v })} data-testid={`admin-user-active-${u.user_id}`} /></label>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export function AdminBookings() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/bookings").then((r) => setItems(r.data)); }, []);
  if (!items.length) return <Empty text="Nessuna prenotazione." testid="admin-no-bookings" />;
  const th = "px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap";
  const td = "px-3 py-3 whitespace-nowrap";
  return (
    <div className="overflow-x-auto rounded-2xl bg-white border border-slate-200" data-testid="admin-bookings">
      <table className="w-full text-sm">
        <thead className="bg-slate-50"><tr>{["Stato", "Immobile", "Inquilino", "Periodo", "Totale inquilino", "Ricarico 10%", "Trattenuta 5%", "Netto proprietario", "Totale HomeToB"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {items.map((b) => (
            <tr key={b.booking_id} className="border-t" data-testid={`admin-booking-${b.booking_id}`}>
              <td className={td}><StatusBadge status={b.status} /></td>
              <td className={`${td} max-w-[220px] truncate`}>{b.listing_title}</td>
              <td className={td}>{b.tenant_name}</td>
              <td className={td}>{fmtDate(b.check_in)} → {fmtDate(b.check_out)} ({b.amounts.months} {t("months")})</td>
              <td className={`${td} font-semibold`}>{eur(b.amounts.total_tenant)}</td>
              <td className={td}>{eur(b.amounts.markup)}</td>
              <td className={td}>{eur(b.amounts.owner_fee)}</td>
              <td className={td}>{eur(b.amounts.owner_net)}</td>
              <td className={`${td} font-bold text-[#FF6B00]`}>{eur(b.amounts.hometob_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExtraRow({ e, onSaved }) {
  const [f, setF] = useState(e);
  const save = async () => { try { await (e.extra_id ? api.put(`/admin/extras/${e.extra_id}`, f) : api.post("/admin/extras", f)); toast.success("Servizio salvato"); onSaved(); } catch (err) { toast.error(errMsg(err)); } };
  const id = e.extra_id || "new";
  return (
    <div className="grid gap-2 sm:grid-cols-[1fr_1fr_140px_120px_auto_auto] items-center rounded-2xl bg-white border border-slate-200 p-3" data-testid={`admin-extra-${id}`}>
      <Input value={f.name_it} onChange={(x) => setF({ ...f, name_it: x.target.value })} placeholder="Nome IT" className="h-10 rounded-lg" data-testid={`extra-name-it-${id}`} />
      <Input value={f.name_en} onChange={(x) => setF({ ...f, name_en: x.target.value })} placeholder="Name EN" className="h-10 rounded-lg" data-testid={`extra-name-en-${id}`} />
      <Select value={f.unit} onValueChange={(v) => setF({ ...f, unit: v })}>
        <SelectTrigger className="h-10 rounded-lg" data-testid={`extra-unit-${id}`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="month">Al mese</SelectItem><SelectItem value="once">Una tantum</SelectItem></SelectContent>
      </Select>
      <Input type="number" min="0" value={f.default_price} onChange={(x) => setF({ ...f, default_price: Number(x.target.value) })} className="h-10 rounded-lg" data-testid={`extra-price-${id}`} />
      <label className="flex items-center gap-2 text-sm">Attivo<Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid={`extra-active-${id}`} /></label>
      <button onClick={save} className="h-10 inline-flex items-center justify-center gap-1 rounded-full bg-[#142B5C] px-4 text-sm font-bold text-white" data-testid={`extra-save-${id}`}>{e.extra_id ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}{e.extra_id ? "Salva" : "Aggiungi"}</button>
    </div>
  );
}

export function AdminExtras() {
  const [items, setItems] = useState([]);
  const [k, setK] = useState(0);
  const load = () => { api.get("/admin/extras").then((r) => setItems(r.data)); setK((x) => x + 1); };
  useEffect(() => { load(); }, []);
  return (
    <div className="grid gap-2" data-testid="admin-extras">
      <p className="text-sm text-slate-500">Catalogo base dei servizi extra: ogni proprietario sceglie quali offrire e a che prezzo (il prezzo qui è il suggerito).</p>
      {items.map((e) => <ExtraRow key={`${e.extra_id}-${k}`} e={e} onSaved={load} />)}
      <ExtraRow key={`new-${k}`} e={{ name_it: "", name_en: "", unit: "month", default_price: 0, active: true }} onSaved={load} />
    </div>
  );
}
